import cx from 'classnames';
import React from 'react';
import ReactDOM from 'react-dom';

import DOM from 'lib/DOM';
import KeyState from 'lib/KeyState';
import LayerPropTypes from 'components/lib/LayerPropTypes';
import LayeredComponentMixin from 'components/mixins/LayeredComponentMixin';
import createChainedFunction from 'utils/createChainedFunction';
import findScrollParent from 'utils/findScrollParent';
import findZIndex from 'utils/findZIndex';
import isFixed from 'utils/isFixed';

import { throttle } from 'lodash';

import styles from './styles/LayerTrigger.css';

const PLACEMENTS = ['right', 'left', 'bottom', 'top'];
const HORIZONTAL_ALIGNMENTS = ['middle', 'left', 'right'];
const VERTICAL_ALIGNMENTS = ['middle', 'top', 'bottom'];

const OPPOSITES = {
  right: 'left',
  left: 'right',
  bottom: 'top',
  top: 'bottom',
};

/**
 * Check if value one is inside or equal to the of value
 *
 * @param {string} one
 * @param {string|array} of
 * @returns {boolean}
 */
function isOneOf(one, of) {
  if (Array.isArray(of)) {
    return of.indexOf(one) >= 0;
  }
  return one === of;
}

/**
 * Used to display a [Layer](/components/Layer) when the trigger element is clicked, hovered, and/or
 * focused.
 *
 * ```example
 *  <div>
 *    <LayerTrigger
 *      layer={
 *        <Menu>
 *          <MenuItem>First item</MenuItem>
 *          <MenuItem>Second item</MenuItem>
 *          <MenuItem>Third item</MenuItem>
 *        </Menu>
 *      }
 *      placement="bottom"
 *      trigger="click">
 *      <Link>Click here to open the menu</Link>
 *    </LayerTrigger>
 *    {' or '}
 *    <LayerTrigger
 *      layer={
 *        <Hovercard>
 *          <HovercardBody>Hello!</HovercardBody>
 *        </Hovercard>
 *      }
 *      trigger="hover">
 *      <Link>hover here to show the hovercard</Link>
 *    </LayerTrigger>
 *  </div>
 * ```
 *
 * # Setting the preferred position of the layer
 *
 * Use the `placement` and `alignment` props of `LayerPropTypes` to set the preferred position of
 * the layer element. LayerTrigger will determine itself whether or not to use another position on
 * the screen if the layer is unable to fit within the viewport.
 *
 * ```example
 *  <LeftRight pull="both">
 *    <LayerTrigger
 *      layer={
 *        <Hovercard>
 *          <HovercardBody>
 *            This layer will be displayed on top of the trigger, most of the time.
 *          </HovercardBody>
 *        </Hovercard>
 *      }
 *      placement="top"
 *      alignment="left"
 *      trigger="hover">
 *      <Link>Hover this link</Link>
 *    </LayerTrigger>
 *    <LayerTrigger
 *      layer={
 *        <Hovercard style={{width: 200}}>
 *          <HovercardBody>
 *            This layer will probably be displayed to the left of the trigger.
 *          </HovercardBody>
 *        </Hovercard>
 *      }
 *      trigger="hover">
 *      <Link>Hover this link</Link>
 *    </LayerTrigger>
 *  </LeftRight>
 * ```
 *
 * # Programmatically triggering a LayerTrigger
 *
 * You can manually show and hide a layer by using `trigger="manual"` on the LayerTrigger and
 * calling `showLayer()` or `hideLayer()` on the LayerTrigger instance.
 *
 * ```js
 *  handleFoo() {
 *    this.refs.trigger.showLayer();
 *  }
 *
 *  render() {
 *    return (
 *      <div>
 *        <LayerTrigger ref="trigger" trigger={['click', 'manual']}>
 *          <Link>Click here</Link>
 *        </LayerTrigger>
 *        <Link onClick={() => this.handleFoo()}>
 *          Or click here!
 *        </Link>
 *      </div>
 *    );
 *  }
 * ```
 *
 * # Showing a Layer when the trigger is mounted
 *
 * The `defaultLayerShown` prop from `LayeredComponentMixin` can be used to have a layer shown by
 * default when the trigger is first mounted (e.g., NUX).
 *
 * ```example
 *  <LayerTrigger
 *    layer={
 *      <Tooltip hideOnBlur={false} hideOnEscape={false}>
 *        Hello!
 *      </Tooltip>
 *    }
 *    trigger="manual"
 *    defaultLayerShown={true}>
 *    <Link>There is a tooltip attached to this link</Link>
 *  </LayerTrigger>
 * ```
 *
 * # Setting the width of the layer to the context element's width
 *
 * The `useContextWidth` prop can be used to set the width of the Layer to be the same as the
 * context width.
 *
 * ```example
 *  <LayerTrigger
 *    layer={
 *      <Layer style={{backgroundColor: '#eee'}}>
 *        This layer is as wide as the button.
 *      </Layer>
 *    }
 *    placement="bottom"
 *    useContextWidth={true}
 *    trigger="click">
 *    <Button>Click to show menu</Button>
 *  </LayerTrigger>
 * ```
 */

const LayerTrigger = React.createClass({
  propTypes: {
    ...LayerPropTypes,
    ...LayeredComponentMixin.propTypes,
    /** A layer instance to display */
    layer: React.PropTypes.node.isRequired,
    /** The type of action that triggers the display of the layer */
    trigger: React.PropTypes.oneOfType([
      React.PropTypes.oneOf(['manual', 'click', 'hover', 'focus']),
      React.PropTypes.arrayOf(React.PropTypes.oneOf(['click', 'hover', 'focus'])),
    ]),
    /** Amount of delay in milliseconds for showing and hiding the layer */
    delay: React.PropTypes.number,
    /** Amount of delay in milliseconds for showing the layer */
    delayShow: React.PropTypes.number,
    /** Amount of delay in milliseconds for hiding the layer */
    delayHide: React.PropTypes.number,
    /** CSS class name to add to the trigger element when the layer is shown */
    activeClassName: React.PropTypes.string,
    /** Sets the width of the layer to the width of the trigger element */
    useContextWidth: React.PropTypes.bool,
    /**
     * HACK. This allows you to override Layer's way of determining z-indices. This should only be
     * used if absolutely necessary (because you are mixing non-Layer overlays)
     */
    zIndex: React.PropTypes.number,
    onShow: React.PropTypes.func,
    onHide: React.PropTypes.func,
    hideLayerOnScroll: React.PropTypes.bool,
  },

  mixins: [
    LayeredComponentMixin,
  ],

  getDefaultProps() {
    return {
      placement: 'right',
      alignment: 'middle',
      trigger: ['hover', 'focus'],
      useContextWidth: false,
    };
  },

  getInitialState() {
    return {
      layerLeft: 0,
      layerRight: null,
      layerTop: -9999,
      layerPlacement: this.props.placement,
      layerAlignment: this.props.alignment,
    };
  },

  componentDidMount() {
    this.updateLayerPosition();
    setTimeout(this.updateLayerPosition, 0);

    if (this.props.hideLayerOnScroll) {
      this._scrollParent = findScrollParent(ReactDOM.findDOMNode(this));
      if (this._scrollParent) {
        this._scrollParent.addEventListener('scroll', this.hideLayer, true);
      }
    }

    window.addEventListener('resize', this.updateLayerPosition);
  },

  componentWillReceiveProps(nextProps) {
    if (nextProps.trigger === 'manual') {
      if (this._hoverDelay !== null) {
        clearTimeout(this._hoverDelay);
        this._hoverDelay = null;
      }
    }
  },

  componentDidUpdate(prevProps, prevState) {
    if (this.state.layerShown !== prevState.layerShown) {
      // this is put at the end of the event queue because
      // the component need to be rendered before we can
      // calculate it's correct size and see if it fits
      // in the view port or not.
      window.setTimeout(this.updateLayerPosition, 0);
    }
  },

  componentWillUnmount() {
    clearTimeout(this._hoverDelay);
    window.removeEventListener('resize', this.updateLayerPosition);

    if (this._scrollParent) {
      this._scrollParent.removeEventListener('scroll', this.hideLayer, true);
    }

    if (this.props.trigger === 'manual' && this.isLayerShown()) {
      this.hideLayer();
    }
  },

  getLayerRect() {
    const layerNode = this.getLayerDOMNode();
    return layerNode.getBoundingClientRect();
  },

  getPosition() {
    const node = ReactDOM.findDOMNode(this);
    const container = this.getContainerDOMNode();

    const offset = container.tagName === 'BODY' ?
      DOM.getOffset(node) : DOM.getPosition(node, container);

    if (isFixed(node)) {
      const scrollPosition = DOM.getScrollPosition();
      offset.left -= scrollPosition.left;
      offset.top -= scrollPosition.top;
    }

    return Object.assign({}, offset, {
      height: node.offsetHeight,
      width: node.offsetWidth,
    });
  },

  _hoverDelay: null,
  _scrollParent: null,

  calcLayerPosition(placement, alignment) {
    const childOffset = this.getPosition();

    const layer = this.getLayerDOMNode();

    const position = {};

    switch (placement) {
      case 'right':
        position.left = childOffset.left + childOffset.width;
        break;
      case 'left':
        position.left = childOffset.left - layer.offsetWidth;
        break;
      case 'top':
        position.top = childOffset.top - layer.offsetHeight;
        break;
      case 'bottom':
        position.top = childOffset.top + childOffset.height;
        break;
      default:
        break;
    }

    if (this.props.useContextWidth && (placement === 'top' || placement === 'bottom')) {
      const width = document.documentElement.clientWidth;
      position.left = childOffset.left;
      position.right = width - (childOffset.left + childOffset.width);
    } else {
      switch (alignment) {
        case 'right':
          position.left = childOffset.left + childOffset.width - layer.offsetWidth;
          break;
        case 'left':
          position.left = childOffset.left;
          break;
        case 'top':
          position.top = childOffset.top;
          break;
        case 'bottom':
          position.top = childOffset.top + childOffset.height - layer.offsetHeight;
          break;
        default:
        case 'middle':
          if (placement === 'left' || placement === 'right') {
            position.top = childOffset.top + childOffset.height / 2 - layer.offsetHeight / 2;
          } else {
            position.left = childOffset.left + childOffset.width / 2 - layer.offsetWidth / 2;
          }
          break;
      }
    }

    return position;
  },

  fitsWithinViewport(left, top, right, bottom) {
    const node = ReactDOM.findDOMNode(this);
    const bounds = isFixed(node) ? DOM.getOffset(document.documentElement) : {
      top: document.body.scrollTop,
      left: document.body.scrollLeft,
    };
    const width = document.documentElement.clientWidth;
    const height = document.documentElement.clientHeight;

    return (
      top >= bounds.top &&
      bottom <= bounds.top + height &&
      left >= bounds.left &&
      right <= bounds.left + width
    );
  },

  handleClick(event) {
    event.preventDefault();
    this.toggleLayer();
  },

  handleDelayedHide() {
    if (this._hoverDelay !== null) {
      clearTimeout(this._hoverDelay);
      this._hoverDelay = null;
      return;
    }

    const delay = this.props.delayHide || this.props.delay;

    if (!delay) {
      this.hideLayer();
      return;
    }

    this._hoverDelay = setTimeout(() => {
      this._hoverDelay = null;
      this.hideLayer();
    }, delay);
  },

  handleDelayedShow() {
    if (this._hoverDelay !== null) {
      clearTimeout(this._hoverDelay);
      this._hoverDelay = null;
      return;
    }

    const delay = this.props.delayShow || this.props.delay;

    if (!delay) {
      this.showLayer();
      return;
    }

    this._hoverDelay = setTimeout(() => {
      this._hoverDelay = null;
      this.showLayer();
    }, delay);
  },

  handleFocus(event) {
    if (!KeyState.isKeyDown()) {
      return;
    }
    this.handleDelayedShow(event);
  },

  updateLayerPosition: throttle(function() {
    if (!this.isMounted() || !this.isLayerShown()) {
      return;
    }

    const layer = this.getLayerDOMNode();
    const width = layer.offsetWidth;
    const height = layer.offsetHeight;

    const attempts = {};
    const { placement, alignment } = this.props;

    const found = [placement, OPPOSITES[placement]].concat(PLACEMENTS).some(pt => {
      if (pt in attempts) {
        return false;
      }
      attempts[pt] = {};

      return [alignment].concat(
        (pt === 'left' || pt === 'right') ? VERTICAL_ALIGNMENTS : HORIZONTAL_ALIGNMENTS
      ).some(at => {
        if (at in attempts[pt]) {
          return false;
        }
        attempts[pt][at] = true;

        const { left, right, top } = this.calcLayerPosition(pt, at);
        if (this.fitsWithinViewport(left, top, left + width, top + height)) {
          this.setState({
            layerLeft: left,
            layerRight: right,
            layerTop: top,
            layerPlacement: pt,
            layerAlignment: at,
          }, function() {
            // NOTE: Hacky but because we don't automatically re-render after the
            // pending state transaction is completed, and LayerWithArrow depends
            // on LayerTrigger to position its arrow, the arrow will never be
            // drawn properly when a Hovercard is first displayed. By rerendering
            // here, we ensure that the child LayerWithArrow updates its arrow
            // position correctly and redraws itself.
            // TODO: Fix this arrow display problem in a less hacky way.
            this.forceUpdate();
          });
          return true;
        }

        return false;
      });
    });

    if (!found) {
      const { left, right, top } = this.calcLayerPosition(placement, alignment);
      this.setState({
        layerLeft: left,
        layerRight: right,
        layerTop: top,
        layerPlacement: placement,
        layerAlignment: alignment,
      }, function() {
        // See above note
        this.forceUpdate();
      });
    }
  }, 100),

  renderLayer() {
    const node = ReactDOM.findDOMNode(this);
    const { layer } = this.props;

    return React.cloneElement(layer, {
      onRequestHide: createChainedFunction(this.hideLayer, layer.props.onRequestHide),
      placement: this.state.layerPlacement,
      alignment: this.state.layerAlignment,
      position: {
        left: this.state.layerLeft,
        right: this.state.layerRight,
        top: this.state.layerTop,
      },
      context: node,
      zIndex: this.props.zIndex || findZIndex(node),
      fixed: isFixed(node),
    });
  },

  render() {
    const props = {};

    const child = React.Children.only(this.props.children);

    if (isOneOf('click', this.props.trigger)) {
      props.onClick = createChainedFunction(this.handleClick, child.props.onClick);
    }

    if (isOneOf('hover', this.props.trigger)) {
      props.onMouseEnter = createChainedFunction(this.handleDelayedShow, child.props.onMouseEnter);
      props.onMouseLeave = createChainedFunction(this.handleDelayedHide, child.props.onMouseLeave);
    }

    if (isOneOf('focus', this.props.trigger)) {
      props.onFocus = createChainedFunction(this.handleFocus, child.props.onFocus);
      props.onBlur = createChainedFunction(this.handleDelayedHide, child.props.onBlur);
    }

    return React.cloneElement(child, Object.assign({}, props, {
      className: cx(styles.this, child.props.className, this.props.className, {
        [this.props.activeClassName]: this.state.layerShown && this.props.activeClassName,
      }),
    }));
  },
});

export default LayerTrigger;



// WEBPACK FOOTER //
// ./src/components/LayerTrigger.jsx